<template>
  <Navigation withExit @back="send('BACK')" @exit="send('EXIT')" />

  <template v-if="shouldShowSendMessageTitle">
    <Title> {{ i18n(translations.sendMessageTitle) }}</Title>
    <p class="text-static-default-low body-1 mb-24 pt-12">
      {{ i18n(translations.sendMessageProblemListInstruction) }}
    </p>
  </template>
  <Title v-else> {{ i18n(translations.title) }}</Title>

  <ul
    class="grid grid-flow-col grid-cols-2 place-content-center"
    :class="[
      { 'grid-rows-4': rowNumber === 4 },
      { 'grid-rows-5': rowNumber === 5 },
    ]"
  >
    <RevListItemInteractive
      v-for="(problem, index) in orderedProblems"
      :key="problem.type"
      :class="[
        'px-0',
        { 'border-static-default-low border-r': index < rowNumber },
      ]"
      items-center
      @click="() => handleProblemClicked(problem.type)"
    >
      <template #label>
        {{ problem.label }}
      </template>

      <template #suffix>
        <IconChevronRight />
      </template>
    </RevListItemInteractive>
  </ul>

  <div class="text-static-default-low caption mt-24 flex items-center">
    {{ i18n(translations.info) }}
    <RevTooltip class="w-[20rem]" position="top">
      <template #trigger="{ hide, show }">
        <IconInfo
          class="ml-4 opacity-[0.8]"
          data-test="info-icon"
          @focusin="show"
          @focusout="hide"
          @mouseenter="show"
          @mouseleave="hide"
        />
      </template>
      {{ i18n(translations.infoTooltip) }}
    </RevTooltip>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevListItemInteractive } from '@ds/components/ListItemInteractive'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconChevronRight } from '@ds/icons/IconChevronRight'
import { IconInfo } from '@ds/icons/IconInfo'

import type { ProblemType } from '~/api/seller-after-sales/types'

import type { MachineSend } from '../../../ActionBar.machine.type'
import Navigation from '../../Navigation/Navigation.vue'
import Title from '../../Title/Title.vue'

import translations from './ProblemListStep.translations'

const i18n = useI18n()

const { problemTypes, send } = defineProps<{
  problemTypes: ProblemType[]
  send: MachineSend
  shouldShowSendMessageTitle?: boolean
}>()

const problemLabels = computed<Record<ProblemType, string>>(() => ({
  OOW_ITEM: i18n(translations.outOfWarranty),
  DAMAGED_PACKAGE: i18n(translations.damagedPackage),
  ACCOUNT_LOCKED: i18n(translations.accountLocked),
  CODE_LOCKED: i18n(translations.codeLocked),
  ITEM_NOT_RECEIVED: i18n(translations.itemNotReceived),
  MISSING_PART: i18n(translations.missingPart),
  WRONG_ITEM: i18n(translations.wrongItem),
  PROBLEM_NOT_IDENTIFIED: i18n(translations.problemNotIdentified),
  OTHER: i18n(translations.other),
}))

const problems = computed(() => {
  const supportedProblemTypes = problemTypes.filter((problemType) => {
    const hasLabel = Boolean(problemLabels.value[problemType])

    return hasLabel
  })

  return supportedProblemTypes.map((problemType) => ({
    type: problemType,
    label: problemLabels.value[problemType],
  }))
})

const orderedProblems = computed(() => {
  const other = problems.value.find((problem) => problem.type === 'OTHER')
  const ordered = problems.value.filter((problem) => problem.type !== 'OTHER')
  if (other) ordered.push(other)

  return ordered
})

const rowNumber = computed(() => Math.ceil(problems.value.length / 2))

function handleProblemClicked(selectedProblemType: ProblemType) {
  switch (selectedProblemType) {
    case 'OOW_ITEM':
      send('OOW_ITEM_SELECTED')
      break
    case 'DAMAGED_PACKAGE':
      send('DAMAGED_PACKAGE_SELECTED')
      break
    case 'ACCOUNT_LOCKED':
      send('ACCOUNT_LOCKED_SELECTED')
      break
    case 'CODE_LOCKED':
      send('CODE_LOCKED_SELECTED')
      break
    case 'ITEM_NOT_RECEIVED':
      send('ITEM_NOT_RECEIVED_SELECTED')
      break
    case 'MISSING_PART':
      send('MISSING_PART_SELECTED')
      break
    case 'WRONG_ITEM':
      send('WRONG_ITEM_SELECTED')
      break
    case 'PROBLEM_NOT_IDENTIFIED':
      send('PROBLEM_NOT_IDENTIFIED_SELECTED')
      break
    case 'OTHER':
      send('OTHER_SELECTED')
      break
    default:
  }
}
</script>
